import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, collection, addDoc, getDocs, updateDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore/lite';
import { getAuth, connectAuthEmulator, signInWithEmailAndPassword, signOut, getIdTokenResult } from 'firebase/auth';

const env = import.meta.env || (typeof process !== 'undefined' ? process.env : {});
const config = {
  apiKey: env.VITE_FIREBASE_API_KEY,
  authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: env.VITE_FIREBASE_APP_ID
};
const useEmulators = env.MODE === 'emulator' && env.VITE_USE_FIREBASE_EMULATORS === 'true' && env.DEV === true;
export const isFirebaseConfigured = Object.values(config).every(value => value && !/your_|DemoKey/.test(value));
let db = null;
let auth = null;
if (isFirebaseConfigured || useEmulators) {
  const app = initializeApp(useEmulators ? { apiKey: 'demo-key', projectId: 'demo-wahitservices', authDomain: 'localhost', appId: 'demo-app' } : config);
  db = getFirestore(app);
  auth = getAuth(app);
  if (useEmulators) {
    connectFirestoreEmulator(db, '127.0.0.1', 8080);
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
  }
}
export { db, auth };
function database() {
  if (!db) throw new Error('Firebase configuration is missing or invalid.');
  return db;
}
function text(value, label, min, max) {
  const result = String(value || '').trim();
  if (result.length < min || result.length > max) throw new Error(`${label} must contain ${min}-${max} characters.`);
  return result;
}
function email(value) {
  const result = text(value, 'Email', 3, 254);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(result)) throw new Error('Enter a valid email address.');
  return result;
}
export async function requireAdmin() {
  if (!auth) throw new Error('Firebase is not configured.');
  await auth.authStateReady();
  if (!auth.currentUser || (await getIdTokenResult(auth.currentUser, true)).claims.admin !== true) {
    throw new Error('Sign in with an authorized administrator account.');
  }
}
export async function signInAdmin(address, password) {
  if (!auth) throw new Error('Firebase is not configured.');
  await signInWithEmailAndPassword(auth, address, password);
  try { await requireAdmin(); } catch (error) { await signOut(auth); throw error; }
}
export async function signOutAdmin() { if (auth) await signOut(auth); }
export async function saveConsultation(data) {
  const payload = {
    name: text(data.name, 'Name', 1, 120), email: email(data.email),
    service: text(data.service || 'General Inquiry', 'Service', 1, 200),
    message: text(data.message, 'Message', 10, 5000), status: 'pending',
    createdAt: new Date().toISOString(), timestamp: serverTimestamp()
  };
  const result = await addDoc(collection(database(), 'consultations'), payload);
  return { success: true, id: result.id, mode: 'cloud' };
}
export async function getConsultations() {
  await requireAdmin();
  const snapshot = await getDocs(query(collection(database(), 'consultations'), orderBy('createdAt', 'desc')));
  return snapshot.docs.map(item => ({ ...item.data(), id: item.id }));
}
export async function updateConsultationStatus(id, status) {
  await requireAdmin();
  if (!['pending', 'reviewed', 'contacted'].includes(status)) throw new Error('Invalid status.');
  await updateDoc(doc(database(), 'consultations', id), { status, updatedAt: new Date().toISOString() });
}
export async function saveNewsletterSubscriber(address) {
  await addDoc(collection(database(), 'subscribers'), { email: email(address), subscribedAt: new Date().toISOString(), timestamp: serverTimestamp() });
  return { success: true };
}
export async function saveChatMessage(sender, message, framework = 'Flutter') {
  try {
    await addDoc(collection(database(), 'chat_sessions'), { sender: text(sender, 'Sender', 1, 20), text: text(message, 'Message', 1, 5000), framework: text(framework, 'Framework', 1, 100), timestamp: serverTimestamp() });
  } catch { /* Optional telemetry must not interrupt the demo. */ }
}
export async function saveAuditLog(message) {
  try { await addDoc(collection(database(), 'audit_logs'), { logMessage: text(message, 'Log', 1, 2000), timestamp: serverTimestamp() }); }
  catch { /* Optional telemetry must not interrupt the demo. */ }
}
